import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import FullWidthModal from '@components/common/Modal/FullWidthModal';
import ModalHeader from '@components/common/ModalHeader';
import cn from 'classnames';
import { normalizeSendDate, lookupItemSendDate, computeHoldTime } from '@utility/helperFunction';
import styles from './holdItemsModal.module.scss';
import HoldController from '../HoldDropdownController/holdController';
import { TouchableButton } from '../../common/Touchable/index';

// Isolated so React.memo prevents re-renders from sibling state changes.
const HoldItemRow = memo(({
  item,
  itemKey,
  sendDate,
  holdMinutes,
  holdDeleted,
  onUpdateHoldState,
  serverAppTheme,
}) => {
  const hasHoldApplied = !!sendDate;

  const handleSetHoldMinutes = useCallback(
    (val) => onUpdateHoldState(itemKey, { holdMinutes: val }),
    [itemKey, onUpdateHoldState]
  );

  const handleSetHoldDeleted = useCallback(
    (val) => onUpdateHoldState(itemKey, { holdDeleted: val }),
    [itemKey, onUpdateHoldState]
  );

  return (
    <div className={cn('d-flex align-items-center', styles.itemRow)}>
      <span
        className={cn('fs_16 fw-600', styles.itemName)}
        style={{ color: serverAppTheme?.primary_text }}
      >
        {item.menuItemName}
      </span>

      <div className={cn('col-9 pr-0 pl-4 d-flex', styles.itemControl)}>
        <HoldController
          isEditMode
          hasHoldApplied={hasHoldApplied}
          holdMinutes={holdMinutes}
          setHoldMinutes={handleSetHoldMinutes}
          holdDeleted={holdDeleted}
          setHoldDeleted={handleSetHoldDeleted}
          sendDate={sendDate}
          serverAppTheme={serverAppTheme}
          dropdownOpensDown
          isFromHoldScreen
        />
      </div>
    </div>
  );
});

HoldItemRow.displayName = 'HoldItemRow';

// ── Hold Items Modal ──────────────────────────────────────────────────────────
const HoldItemsModal = ({
  isOpen,
  onClose,
  onApplyHold,
  items,
  checkOrders,
  seat,
  serverAppTheme,
}) => {
  const [holdStates, setHoldStates] = useState({});

  // Single source of truth for per-item sendDate and hasHoldApplied
  const itemMetaMap = useMemo(() => {
    const orderItems = checkOrders?.[0]?.items ?? [];
    return (items ?? []).reduce((acc, item) => {
      const itemKey = item?.internalId ?? item?.id;
      const sendDate = normalizeSendDate(lookupItemSendDate(orderItems, item.id));
      acc[itemKey] = { sendDate, hasHoldApplied: !!sendDate };
      return acc;
    }, {});
  }, [items, checkOrders]);

  // Snapshot initial hold state when modal opens
  useEffect(() => {
    if (!isOpen || !items?.length) return;
    setHoldStates(
      items.reduce((acc, item) => {
        const itemKey = item?.internalId ?? item?.id;
        acc[itemKey] = {
          holdMinutes: 0,
          holdDeleted: false,
          sendDate: itemMetaMap?.[itemKey]?.sendDate ?? null,
        };
        return acc;
      }, {})
    );
  }, [isOpen, items, itemMetaMap]);

  const onUpdateHoldState = useCallback((internalId, updates) => {
    setHoldStates((prev) => ({
      ...prev,
      [internalId]: { ...prev[internalId], ...updates },
    }));
  }, []);

  const handleApplyHold = useCallback(() => {
    const itemsWithHoldTime = items.map(item => {
      const itemKey = item?.internalId ?? item?.id;
      const { hasHoldApplied } = itemMetaMap[itemKey] ?? {};
      const { holdMinutes = 0, holdDeleted = false } = holdStates[itemKey] ?? {};
      return { ...item, holdTime: computeHoldTime(hasHoldApplied, holdMinutes, holdDeleted) };
    });
    onApplyHold(itemsWithHoldTime);
  }, [items, holdStates, itemMetaMap, onApplyHold]);

  const hasChanges = useMemo(
    () =>
      Object.values(holdStates).some(({ sendDate, holdMinutes = 0, holdDeleted = false }) => {
        if (!!sendDate && holdDeleted) return true;
        if (holdMinutes > 0) return true;
        return false;
      }),
    [holdStates]
  );

  const checkItems = useMemo(() => checkOrders?.[0]?.items || [], [checkOrders]);

  const getSendDate = useCallback(
    (item) => normalizeSendDate(lookupItemSendDate(checkItems, item.id)),
    [checkItems]
  );

  const handleApplyAllMinutes = useCallback((val) => {
    if (val === 0) return;
    setHoldStates((prev) => {
      const nextStates = { ...prev };
      let applied = false;
      items.forEach((item) => {
        const itemKey = item?.internalId ?? item?.id;
        if (!getSendDate(item)) {
          nextStates[itemKey] = { ...nextStates[itemKey], holdMinutes: val, holdDeleted: false };
          applied = true;
        }
      });
      return applied ? nextStates : prev;
    });
  }, [items, getSendDate]);

  const cancelBtnStyle = useMemo(() => ({
    color: serverAppTheme?.cancleBtn_font,
    backgroundColor: serverAppTheme?.OFF_Premise_Btn,
    border: serverAppTheme?.cancleBtn_Border,
  }), [serverAppTheme]);

  const applyBtnStyle = useMemo(() => ({
    backgroundColor: hasChanges ? serverAppTheme?.secondaryBtn : serverAppTheme?.secondaryBtnDisabled,
    color: hasChanges ? serverAppTheme?.btn_font_color : serverAppTheme?.btn_font_color_disabled,
  }), [hasChanges, serverAppTheme]);

  return (
    <div className="h-100">
    <FullWidthModal
      isOpen={isOpen}
      style={{ backgroundColor: serverAppTheme?.popUp_bg, display: 'flex', flexDirection: 'column' }}
    >
      <ModalHeader
        isTab={!!seat?.tab}
        stationName={seat?.stationName || ''}
        tileHeader="Hold Items"
        seatNumber={seat?.number || ''}
        customHeight="35px"
      />
      <div className="d-flex flex-column" style={{ height: '420px' }}>
    
      <div
        className={cn('text-center pt-3 pb-4 fs_16 fw-400', styles.subtitle)}
        style={{ color: serverAppTheme?.primary_text }}
      >
        Select a hold time and click Apply Hold
      </div>

      {/* <div className="col-8 mx-auto"> */}
      {items?.length > 1 && (
        <div className="w-50 align-self-center px-3 mx-auto">
          <div className={cn('d-flex align-items-center', styles.itemRow)}>
            <span
              className={cn('fs_16 fw-600', styles.itemName)}
              style={{ color: serverAppTheme?.primary_text }}
            >
              Apply to All
            </span>
            <div className={cn('col-9 pr-0 pl-4 d-flex', styles.itemControl)}>
              <HoldController
                isEditMode={false}
                hasHoldApplied={false}
                holdMinutes={0}
                setHoldMinutes={handleApplyAllMinutes}
                setHoldDeleted={() => {}}
                serverAppTheme={serverAppTheme}
                dropdownOpensDown={true}
                isFromHoldScreen={true}
              />
            </div>
          </div>
          <span
            className={cn('fs_16 fw-600 justify-content-center d-flex py-2')}
            style={{ color: serverAppTheme?.primary_text }}
          >
            OR
          </span>
        </div>
      )}

      <div className={cn('w-50 align-self-center flex-grow-1 overflowY px-3 mx-auto', styles.itemsContainer)}
       style={{ flex: "1 1 0%" }}
       >
        {items?.map((item) => {
          const itemKey = item?.internalId ?? item?.id;
          const { sendDate } = itemMetaMap[itemKey] ?? {};
          const { holdMinutes = 0, holdDeleted = false } = holdStates[itemKey] ?? {};
          return (
            <HoldItemRow
              key={itemKey}
              item={item}
              itemKey={itemKey}
              sendDate={sendDate}
              holdMinutes={holdMinutes}
              holdDeleted={holdDeleted}
              onUpdateHoldState={onUpdateHoldState}
              serverAppTheme={serverAppTheme}
            />
          );
        })}
      </div>
      </div>

      <div className={cn('w-60 mx-auto px-3 pb-3', styles.footer)}>
        <div className="d-flex w-100">
          <TouchableButton
            type="button"
            className={cn('fs_16 fw-600', styles.btn, styles.btnCancel)}
            style={cancelBtnStyle}
            onClick={onClose}
          >
            Cancel
          </TouchableButton>
          <TouchableButton
            type="button"
            className={cn('fs_16 fw-600', styles.btn, styles.btnApply)}
            style={applyBtnStyle}
            disabled={!hasChanges}
            onClick={handleApplyHold}
          >
            Apply Hold
          </TouchableButton>
        </div>
      </div>
    </FullWidthModal>
    </div>
  );
};

HoldItemsModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onApplyHold: PropTypes.func.isRequired,
  items: PropTypes.array,
  checkOrders: PropTypes.array,
  seat: PropTypes.shape({
    tab: PropTypes.any,
    stationName: PropTypes.string,
    number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  }),
  serverAppTheme: PropTypes.object,
};

HoldItemsModal.defaultProps = {
  items: [],
  checkOrders: [],
  seat: {},
  serverAppTheme: {},
};

export default memo(HoldItemsModal);
